import { Button } from "@/components/flexkit/Button";
import { IconArrowDown } from "@/assets/icons/geist/IconArrowDown";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Avatar, AvatarImage, AvatarFallback, AvatarInitials } from "@/components/Avatar";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { CircleAlert } from "lucide-react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { getRepoUrl } from "@/repo-url";
import {
    useDeleteEnvironment,
    useListEnvironmentInventory,
    useStopEnvironment,
    type PlainEnvironment,
    type UseListEnvironmentInventoryParams,
} from "@/queries/environment-queries";
import { useGetOrganizationPolicies, useMembers, type PlainOrganizationMember } from "@/queries/organization-queries";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import { useListServiceAccounts, type PlainServiceAccount } from "@/queries/service-account-queries";
import { useListRunners, type PlainRunner } from "@/queries/runner-queries";
import { EnvironmentPhaseTag } from "@/routes/environments/EnvironmentPhaseTag";
import { effectiveState } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { Timestamp } from "@bufbuild/protobuf";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useSearchParams } from "react-router-dom";
import { TrackLocations } from "@/hooks/use-segment";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { FilterSelect } from "@/components/FilterSelect";
import { Tooltip } from "@/components/Tooltip";

export const Inventory: FC = () => {
    const [searchParams] = useSearchParams();
    const runnerFilter = searchParams.get("runner") || undefined;
    const runnerKind = useMemo(() => {
        for (const opt of runnerKindOptions) {
            if (runnerFilter === opt.key) {
                return opt.kind;
            }
        }
        return undefined;
    }, [runnerFilter]);
    const runnerID = useMemo(() => {
        // If the runner filter is not a runner kind, it's a runner ID
        return runnerKind ? undefined : runnerFilter;
    }, [runnerKind, runnerFilter]);
    const creatorID = searchParams.get("creator") || undefined;

    const { data: runners, isLoading: isLoadingRunners } = useListRunners({});
    const { data: membersData, isLoading: isLoadingMembers } = useMembers();
    const { data: serviceAccountsData, isLoading: isLoadingServiceAccounts } = useListServiceAccounts();
    const { data: projectsData, isLoading: isLoadingProjects } = useListProjects();
    const { data: policiesData, isLoading: isLoadingPolicies } = useGetOrganizationPolicies();
    const [filterValue, setFilterValue] = useState<FilterValue>({ runnerID, creatorID, runnerKind });

    const handleFilterChange = useCallback((value: FilterValue) => {
        setFilterValue(value);
    }, []);

    const maxEnvLifetime = policiesData?.maximumEnvironmentLifetime?.seconds
        ? Number(policiesData.maximumEnvironmentLifetime.seconds)
        : 0;
    const listEnvironmentFilter = useMemo<UseListEnvironmentInventoryParams>(() => {
        return {
            runnerID: filterValue.runnerID,
            creatorID: filterValue.creatorID,
            projectID: filterValue.projectID,
            phase: filterValue.phase,
            runnerKind: filterValue.runnerKind,
            createdBefore:
                filterValue.status === LIFETIME_POLICY_EXCEEDED && maxEnvLifetime > 0
                    ? new Date(Date.now() - maxEnvLifetime * 1000)
                    : undefined,
        };
    }, [filterValue, maxEnvLifetime]);

    const isLoading =
        isLoadingRunners || isLoadingMembers || isLoadingServiceAccounts || isLoadingProjects || isLoadingPolicies;
    if (isLoading) {
        return <LoadingState />;
    }

    return (
        <div className="flex h-full flex-col">
            <div className="text-base">Inventory of running and stopped environments in your organization.</div>
            <Filter
                runners={runners || []}
                members={membersData?.members || []}
                serviceAccounts={serviceAccountsData?.serviceAccounts || []}
                projects={projectsData?.projects || []}
                initialValue={{ runnerID, creatorID, runnerKind }}
                onFilterChange={handleFilterChange}
                className="pt-4"
            />
            <InventoryTable
                filterValue={listEnvironmentFilter}
                runners={runners || []}
                members={membersData?.members || []}
                serviceAccounts={serviceAccountsData?.serviceAccounts || []}
                projects={projectsData?.projects || []}
                maxEnvLifetime={maxEnvLifetime}
                className="flex flex-grow flex-col pt-4"
            />
        </div>
    );
};

type FilterValue = {
    runnerID?: string;
    creatorID?: string;
    projectID?: string;
    phase?: EnvironmentPhase;
    runnerKind?: RunnerKind;
    status?: string;
};

type FilterProps = {
    initialValue: FilterValue;
    runners: PlainRunner[];
    members: PlainOrganizationMember[];
    serviceAccounts: PlainServiceAccount[];
    projects: PlainProject[];
    onFilterChange?: (value: FilterValue) => void;
};

const allPhases: Record<string, EnvironmentPhase> = {
    Creating: EnvironmentPhase.CREATING,
    Starting: EnvironmentPhase.STARTING,
    Running: EnvironmentPhase.RUNNING,
    Updating: EnvironmentPhase.UPDATING,
    Stopping: EnvironmentPhase.STOPPING,
    Stopped: EnvironmentPhase.STOPPED,
    Deleting: EnvironmentPhase.DELETING,
    Deleted: EnvironmentPhase.DELETED,
};

const LIFETIME_POLICY_EXCEEDED = "Lifetime Policy Exceeded";
const allStatuses: Record<string, string> = {
    [LIFETIME_POLICY_EXCEEDED]: LIFETIME_POLICY_EXCEEDED,
};

// Add additional filter items for these kinds in the runner filter.
// Allows selecting all local or all remote runners.
const runnerKindOptions = [
    {
        kind: RunnerKind.LOCAL,
        key: "local",
        name: "All Ona Desktop runners",
    },
    {
        kind: RunnerKind.REMOTE,
        key: "remote",
        name: "All remote runners",
    },
];

const Filter: FC<FilterProps & PropsWithClassName> = ({
    className,
    runners,
    members,
    serviceAccounts,
    projects,
    initialValue,
    onFilterChange,
}) => {
    const [prevFilterValue, setPrevFilterValue] = useState<FilterValue>(initialValue);

    const [selectedRunnerID, setSelectedRunnerID] = useState<string | undefined>(initialValue.runnerID);
    const [selectedMemberID, setSelectedMemberID] = useState<string | undefined>(initialValue.creatorID);
    const [selectedProjectID, setSelectedProjectID] = useState<string | undefined>(initialValue.projectID);
    const [selectedRunnerKind, setSelectedRunnerKind] = useState<RunnerKind | undefined>(initialValue.runnerKind);
    const [selectedPhase, setSelectedPhase] = useState<string | undefined>();
    const [selectedStatus, setSelectedStatus] = useState<string | undefined>();

    const setSelectedRunner = useCallback((value: string) => {
        for (const opt of runnerKindOptions) {
            if (value === opt.key) {
                // Value is a runner kind.
                setSelectedRunnerKind(opt.kind);
                setSelectedRunnerID(undefined);
                return;
            }
        }

        // If the value is not a runner kind, it's a runner ID.
        setSelectedRunnerKind(undefined);
        setSelectedRunnerID(value);
    }, []);

    const debouncedFilterChanged = useDebouncedCallback((filter: FilterValue) => {
        if (!onFilterChange) {
            return;
        }
        onFilterChange(filter);
    }, 100);

    useEffect(() => {
        debouncedFilterChanged({ ...prevFilterValue });
    }, [prevFilterValue, debouncedFilterChanged]);

    useEffect(() => {
        setPrevFilterValue((prev) => {
            if (
                selectedRunnerID !== prev.runnerID ||
                selectedMemberID !== prev.creatorID ||
                selectedProjectID !== prev.projectID ||
                selectedRunnerKind !== prev.runnerKind ||
                selectedPhase !== prev.phase ||
                selectedStatus !== prev.status
            ) {
                return {
                    ...prev,
                    runnerID: selectedRunnerID,
                    creatorID: selectedMemberID,
                    projectID: selectedProjectID,
                    runnerKind: selectedRunnerKind,
                    phase: selectedPhase ? allPhases[selectedPhase] : undefined,
                    status: selectedStatus ? allStatuses[selectedStatus] : undefined,
                };
            }
            return prev;
        });
    }, [selectedRunnerID, selectedMemberID, selectedProjectID, selectedRunnerKind, selectedPhase, selectedStatus]);

    const runnerItems = useMemo(() => {
        const items = new Map<string, string>();
        for (const opt of runnerKindOptions) {
            items.set(opt.key, opt.name);
        }
        for (const r of runners) {
            items.set(r.runnerId, r.name);
        }
        return items;
    }, [runners]);

    const creatorItems = useMemo(() => {
        const combined: [string, string][] = [
            ...members.map((m) => [m.userId, m.fullName] as [string, string]),
            ...serviceAccounts.map((sa) => [sa.id, sa.name] as [string, string]),
        ];
        combined.sort((a, b) => a[1].localeCompare(b[1]));
        return new Map(combined);
    }, [members, serviceAccounts]);

    const selectedRunner = useMemo(() => {
        for (const opt of runnerKindOptions) {
            if (selectedRunnerKind === opt.kind) {
                return opt.key;
            }
        }

        return selectedRunnerID;
    }, [selectedRunnerID, selectedRunnerKind]);

    return (
        <div className={cn("flex flex-row gap-2", className)}>
            <FilterSelect
                placeholder="All projects"
                className="w-3/12"
                key={"projects-filter"}
                initialValue={selectedProjectID}
                items={new Map(projects.map((r) => [r.id, r.metadata?.name || r.id]))}
                onChange={setSelectedProjectID}
            />
            <FilterSelect
                placeholder="All members"
                className="w-3/12"
                key={"mermbers-filter"}
                initialValue={selectedMemberID}
                items={creatorItems}
                onChange={setSelectedMemberID}
            />
            <FilterSelect
                placeholder="All runners"
                className="w-3/12"
                key={"runners-filter"}
                initialValue={selectedRunner}
                items={runnerItems}
                onChange={setSelectedRunner}
            />
            <FilterSelect
                placeholder="All phases"
                className="w-3/12"
                key={"phases-filter"}
                initialValue={selectedPhase}
                items={new Map(Object.keys(allPhases).map((r) => [r, r]))}
                onChange={setSelectedPhase}
            />
            <FilterSelect
                placeholder="All statuses"
                className="w-3/12"
                key={"statuses-filter"}
                initialValue={selectedStatus}
                items={new Map(Object.keys(allStatuses).map((r) => [r, r]))}
                onChange={setSelectedStatus}
            />
        </div>
    );
};

type InventoryTableProps = {
    filterValue: UseListEnvironmentInventoryParams;
    runners: PlainRunner[];
    members: PlainOrganizationMember[];
    serviceAccounts: PlainServiceAccount[];
    projects: PlainProject[];
    maxEnvLifetime: number;
};

const InventoryTable: FC<InventoryTableProps & PropsWithClassName> = ({
    filterValue,
    className,
    members,
    serviceAccounts,
    runners,
    projects,
    maxEnvLifetime,
}) => {
    const { data, hasNextPage, fetchNextPage } = useListEnvironmentInventory({ ...filterValue });
    const environments = data?.pages.flatMap((page) => page.environments) || [];

    const projectsMap = useMemo(() => {
        const map = new Map<string | undefined, PlainProject>();
        for (const p of projects) {
            map.set(p.id, p);
        }
        return map;
    }, [projects]);
    const membersMap = useMemo(() => {
        const map = new Map<string | undefined, PlainOrganizationMember>();
        for (const m of members) {
            map.set(m.userId, m);
        }
        return map;
    }, [members]);
    const serviceAccountsMap = useMemo(() => {
        const map = new Map<string | undefined, PlainServiceAccount>();
        for (const sa of serviceAccounts) {
            map.set(sa.id, sa);
        }
        return map;
    }, [serviceAccounts]);
    const runnersMap = useMemo(() => {
        const map = new Map<string | undefined, PlainRunner>();
        for (const r of runners) {
            map.set(r.runnerId, r);
        }
        return map;
    }, [runners]);

    const isEmpty = environments.length === 0;
    if (isEmpty) {
        const anyFilters =
            filterValue.runnerID ||
            filterValue.creatorID ||
            filterValue.projectID ||
            filterValue.phase ||
            filterValue.createdBefore;

        if (anyFilters) {
            return (
                <div className="mt-8">
                    <EmptyStateCard
                        title="No matches"
                        description="No environments matching the filters."
                        data-testid="no-matches-empty-state"
                    />
                </div>
            );
        } else {
            return (
                <div className="mt-8">
                    <EmptyStateCard
                        title="It's quiet here"
                        description="Currently, there are no environments in your organization."
                        data-testid="no-environments-empty-state"
                    />
                </div>
            );
        }
    }

    return (
        <div className={cn("flex flex-col", className)}>
            <div>
                <Table>
                    <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                        <TableRow className="h-8 text-base text-content-strong">
                            <TableHead className="w-[22%] pl-3 xl:w-[26%]">Project</TableHead>
                            <TableHead className="pl-3">Member</TableHead>
                            <TableHead className="pl-3">Runner</TableHead>
                            <TableHead className="pl-3">Created</TableHead>
                            <TableHead className="pl-3">Last started</TableHead>
                            <TableHead className="pl-3">Phase</TableHead>
                            <TableHead className="w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody className="border-t border-border-light text-base">
                        {environments.map((environment) => {
                            const creatorId = environment.metadata?.creator?.id;
                            const member = membersMap.get(creatorId);
                            const serviceAccount = serviceAccountsMap.get(creatorId);
                            return (
                                <InventoryItem
                                    key={"item-" + environment.id}
                                    environment={environment}
                                    member={member}
                                    serviceAccount={serviceAccount}
                                    runner={runnersMap.get(environment.metadata?.runnerId)}
                                    project={projectsMap.get(environment.metadata?.projectId)}
                                    maxEnvLifetime={maxEnvLifetime}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
            <div className="flex w-full flex-row justify-center py-8">
                {hasNextPage && (
                    <Button
                        variant={"ghost"}
                        title="Load more"
                        onClick={() => fetchNextPage()}
                        data-tracking-id="load-more-environments-inventory-table"
                    >
                        <IconArrowDown size="lg" /> Load more
                    </Button>
                )}
            </div>
        </div>
    );
};

type InventoryItemProps = {
    environment: PlainEnvironment;
    member?: PlainOrganizationMember;
    serviceAccount?: PlainServiceAccount;
    runner?: PlainRunner;
    project?: PlainProject;
    maxEnvLifetime: number;
};

const InventoryItem: FC<InventoryItemProps & PropsWithClassName> = ({
    environment,
    runner,
    member,
    serviceAccount,
    project,
    maxEnvLifetime,
}) => {
    const creatorName = member?.fullName || serviceAccount?.name;
    const creatorAvatarUrl = member?.avatarUrl;
    const [showModal, setShowModal] = useState<"stop" | "delete" | "force-delete" | undefined>(undefined);

    const isLifetimeExceeded = useMemo(() => {
        if (maxEnvLifetime <= 0) {
            return false;
        }
        if (!environment.metadata?.createdAt) {
            return false;
        }
        const createdAt = new Timestamp(environment.metadata.createdAt).toDate().getTime();
        return Date.now() - createdAt > maxEnvLifetime * 1000;
    }, [environment.metadata?.createdAt, maxEnvLifetime]);

    const createdTime = useMemo(
        () =>
            environment.metadata?.createdAt
                ? new Timestamp(environment.metadata?.createdAt).toDate().toLocaleDateString()
                : "",
        [environment.metadata?.createdAt],
    );

    const lastStarted = useMemo(
        () =>
            environment.metadata?.lastStartedAt
                ? new Timestamp(environment.metadata?.lastStartedAt).toDate().toLocaleDateString()
                : "",
        [environment.metadata?.lastStartedAt],
    );

    const state = useMemo(() => {
        if (environment) {
            return effectiveState(environment);
        }
        return;
    }, [environment]);

    const { toast } = useToast();

    const stopEnvironment = useStopEnvironment();
    const handleStop = useCallback(() => {
        stopEnvironment.mutate(environment.id, {
            onError: (e) => {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(e),
                });
            },
        });
    }, [stopEnvironment, toast, environment.id]);

    const deleteEnvironment = useDeleteEnvironment();
    const handleDelete = useCallback(() => {
        deleteEnvironment.mutate(
            { environmentId: environment.id, force: false },
            {
                onError: (e) => {
                    toast({
                        title: "Failed to delete environment",
                        description: formatError(e),
                    });
                },
            },
        );
    }, [deleteEnvironment, environment.id, toast]);

    const handleForceDelete = useCallback(() => {
        deleteEnvironment.mutate(
            { environmentId: environment.id, force: true },
            {
                onError: (e) => {
                    toast({
                        title: "Failed to force delete environment",
                        description: formatError(e),
                    });
                },
            },
        );
    }, [deleteEnvironment, environment.id, toast]);

    const projectOrRepo = useMemo(() => {
        if (project?.metadata?.name) {
            return project?.metadata?.name;
        }
        return getRepoUrl(environment)?.repoUrl?.replace("https://github.com/", "") || "loading...";
    }, [environment, project?.metadata?.name]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    return (
        <>
            <TableRow className="h-16 border-border-light">
                <TableCell>
                    <Text className="truncate text-base">{projectOrRepo}</Text>
                </TableCell>
                <TableCell>
                    <div className="inline-flex grow items-center space-x-2">
                        <Avatar size={32} className="hidden xl:inline-flex">
                            {creatorAvatarUrl && (
                                <AvatarImage src={creatorAvatarUrl} alt={`${creatorName || "Unknown"}'s avatar`} />
                            )}
                            <AvatarFallback>
                                <AvatarInitials name={creatorName || "Unknown"} size={32} />
                            </AvatarFallback>
                        </Avatar>
                        <Text className="text-base">{creatorName}</Text>
                    </div>
                </TableCell>
                <TableCell>
                    <Text className="text-base">{runner?.name}</Text>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-1">
                        <Text className={cn("truncate text-base", isLifetimeExceeded && "text-content-red")}>
                            {createdTime}
                        </Text>
                        {isLifetimeExceeded && (
                            <Tooltip content="Environment lifetime exceeded">
                                <CircleAlert className="h-4 w-4 text-content-red" />
                            </Tooltip>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    <Text className="truncate text-base">{lastStarted}</Text>
                </TableCell>
                <TableCell>
                    {state && (
                        <>
                            <div className="hidden max-w-[140px] xl:block">
                                <EnvironmentPhaseTag state={state} truncate />
                            </div>
                            <div className="block xl:hidden">
                                <EnvironmentPhaseTag state={state} variant="dot" />
                            </div>
                        </>
                    )}
                </TableCell>
                <TableCell>
                    <div className="flex flex-row justify-end">
                        <DropdownActions>
                            <DropdownMenuItem
                                onClick={handleCopyId}
                                data-tracking-id="copy-environment-id-environment-inventory-item"
                            >
                                Copy ID
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                disabled={environment.status?.phase !== EnvironmentPhase.RUNNING}
                                onClick={() => setShowModal("stop")}
                                data-tracking-id="show-stop-environment-modal-environment-inventory-item"
                            >
                                Stop
                            </DropdownMenuItem>
                            {/* Show Delete, when the Environment has not yet been requested to delete */}
                            {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                                <DropdownMenuItem
                                    onClick={() => setShowModal("delete")}
                                    className="text-red-500"
                                    data-tracking-id="show-delete-environment-modal-environment-inventory-item"
                                >
                                    Delete
                                </DropdownMenuItem>
                            )}
                            {/* Show Force Delete, when the Environment is in Deleting state */}
                            {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && (
                                <DropdownMenuItem
                                    onClick={() => setShowModal("force-delete")}
                                    className="text-red-500"
                                    data-tracking-id="show-force-delete-environment-modal-environment-inventory-item"
                                >
                                    Force Delete
                                </DropdownMenuItem>
                            )}
                        </DropdownActions>
                    </div>
                </TableCell>
            </TableRow>
            {showModal === "stop" && (member || serviceAccount) && runner && (
                <StopEnvironmentModal
                    environment={environment}
                    project={project}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleStop();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
            {showModal === "delete" && (member || serviceAccount) && runner && (
                <DeleteEnvironmentModal
                    environment={environment}
                    project={project}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleDelete();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
            {showModal === "force-delete" && (member || serviceAccount) && runner && (
                <ForceDeleteEnvironmentModal
                    environment={environment}
                    project={project}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleForceDelete();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
        </>
    );
};

const StopEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.InventoryStopEnvironmentModal}>
                <DialogHeader>
                    <DialogTitle>Stop environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>This will stop the service account&apos;s environment.</span>
                    ) : (
                        <span>
                            This will stop the user&apos;s environment. They won&apos;t lose any changes.
                        </span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve stopped their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-stop-environment-inventory-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-stop-environment-inventory-modal"
                    >
                        Stop
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const DeleteEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.InventoryDeleteEnvironmentModal}>
                <DialogHeader>
                    <DialogTitle>Delete environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>This will delete the service account&apos;s environment.</span>
                    ) : (
                        <span>
                            This will delete the user&apos;s environment. They may lose any uncommitted changes.
                        </span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve deleted their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-delete-environment-inventory-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-delete-environment-inventory-modal"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const ForceDeleteEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent
                className="max-w-lg"
                data-track-location={TrackLocations.InventoryForceDeleteEnvironmentModal}
            >
                <DialogHeader>
                    <DialogTitle>Irreversible Action</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>
                            This will force delete the service account&apos;s environment, which may leave some
                            infrastructure or files behind that require manual cleanup.
                        </span>
                    ) : (
                        <span>
                            This will force delete the user&apos;s environment, which may leave some infrastructure or
                            files behind that require manual cleanup.
                        </span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve force deleted their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-force-delete-environment-force-delete-environment-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-force-delete-environment-force-delete-environment-modal"
                    >
                        Force Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type InventoryBannerProps = {
    environment: PlainEnvironment;
    member?: PlainOrganizationMember;
    serviceAccount?: PlainServiceAccount;
    runner: PlainRunner;
    project?: PlainProject;
} & PropsWithClassName;

const InventoryBanner: FC<InventoryBannerProps> = (p) => {
    const creatorName = p.member?.fullName || p.serviceAccount?.name;
    const creatorAvatarUrl = p.member?.avatarUrl;

    const projectOrRepo = useMemo(() => {
        if (p.project?.metadata?.name) {
            return p.project?.metadata?.name;
        }
        return getRepoUrl(p.environment)?.repoUrl?.replace("https://github.com/", "") || "loading...";
    }, [p.environment, p.project?.metadata?.name]);
    return (
        <div
            className={cn(
                "flex w-full flex-row rounded-lg border border-solid border-border-light bg-surface-secondary px-4 py-2",
                p.className,
            )}
        >
            <div className="flex flex-grow flex-col">
                <Text className="text-base font-bold">{projectOrRepo}</Text>
                <Text className="text-base">{p.runner.name}</Text>
            </div>
            <div className="inline-flex items-center space-x-2">
                <Avatar size={32}>
                    {creatorAvatarUrl && (
                        <AvatarImage src={creatorAvatarUrl} alt={`${creatorName || "Unknown"}'s avatar`} />
                    )}
                    <AvatarFallback>
                        <AvatarInitials name={creatorName || "Unknown"} size={32} />
                    </AvatarFallback>
                </Avatar>
                <Text className="text-base">{creatorName}</Text>
            </div>
        </div>
    );
};
